import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, fmtUsd, fmtSigned, fmtDate, fmtNum, fixed } from "@/lib/journal";
import { Trash, ArrowsClockwise, Plus } from "@phosphor-icons/react";

const Stat = ({ label, value, tone = "neutral", testid }) => (
  <div data-testid={testid} className="border border-[#1F1F1F] bg-surface p-6">
    <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</p>
    <p
      className={`mt-5 font-mono text-2xl sm:text-3xl tracking-tight ${
        tone === "pos" ? "text-[#4ADE80]" : tone === "neg" ? "text-[#EF4444]" : "text-white"
      }`}
    >
      {value}
    </p>
  </div>
);

export const BasketPanel = ({ portfolio, onChanged }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [price, setPrice] = useState("");
  const [balance, setBalance] = useState("");
  const [entry, setEntry] = useState({ date: "", usd: "", eth: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/portfolios/${portfolio.id}/basket`);
      setData(data);
      setPrice(String(data.lp_price_usd || ""));
      setBalance(String(data.lp_balance || ""));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("basket.loadFailed"));
    }
  }, [portfolio.id, t]);

  useEffect(() => { load(); }, [load]);

  const savePosition = async () => {
    setBusy(true);
    try {
      await api.put(`/portfolios/${portfolio.id}`, {
        lp_balance: Number(balance) || 0,
        lp_price_usd: Number(price) || 0,
      });
      toast.success(t("basket.priceSaved"));
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("basket.saveFailed"));
    } finally {
      setBusy(false);
    }
  };

  const addEntry = async () => {
    if (!entry.date || !Number(entry.usd)) return toast.error(t("basket.entryRequired"));
    setBusy(true);
    try {
      await api.post(`/portfolios/${portfolio.id}/basket/entries`, {
        date: entry.date,
        usd: Number(entry.usd),
        eth: Number(entry.eth) || null,
      });
      toast.success(t("basket.entryAdded"));
      setEntry({ date: "", usd: "", eth: "" });
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("basket.saveFailed"));
    } finally {
      setBusy(false);
    }
  };

  const removeEntry = async (id) => {
    try {
      await api.delete(`/trades/${id}`);
      toast.success(t("basket.entryRemoved"));
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("basket.saveFailed"));
    }
  };

  if (!data) return <p className="font-mono text-sm text-zinc-600">{t("common.loading")}</p>;
  const pos = data.pnl >= 0;

  return (
    <div className="space-y-10" data-testid="basket-panel">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Stat testid="basket-invested" label={t("basket.invested")} value={fmtUsd(data.invested)} />
        <Stat testid="basket-value" label={t("basket.currentValue")} value={fmtUsd(data.current_value)} />
        <Stat testid="basket-pnl" label={t("basket.pnl")} value={fmtSigned(data.pnl)} tone={pos ? "pos" : "neg"} />
        <Stat
          testid="basket-pnl-pct"
          label={t("basket.pnlPct")}
          value={`${pos ? "+" : ""}${fmtNum(data.pnl_pct, 2)}%`}
          tone={pos ? "pos" : "neg"}
        />
      </div>

      <div className="border border-[#1F1F1F] bg-surface p-6" data-testid="basket-position">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
          {t("basket.position")} {data.lp_symbol ? `· ${data.lp_symbol}` : ""}
        </h3>
        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t("basket.lpBalance")}</Label>
            <Input
              data-testid="basket-balance-input"
              value={balance}
              onChange={(e) => setBalance(e.target.value)}
              className="rounded-none border-[#1F1F1F] bg-[#050505] font-mono focus-visible:ring-[#C58361]"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t("basket.lpPrice")}</Label>
            <Input
              data-testid="basket-price-input"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              className="rounded-none border-[#1F1F1F] bg-[#050505] font-mono focus-visible:ring-[#C58361]"
            />
          </div>
          <div className="flex items-end">
            <Button
              data-testid="basket-save-price-btn"
              onClick={savePosition}
              disabled={busy}
              className="h-10 w-full rounded-none bg-[#C58361] text-sm font-medium text-black hover:bg-[#A86B4C]"
            >
              <ArrowsClockwise size={14} className="mr-2" /> {t("basket.updatePrice")}
            </Button>
          </div>
        </div>
        <p className="mt-3 font-mono text-[10px] text-zinc-600">{t("basket.priceHint")}</p>
      </div>

      <div className="border border-[#1F1F1F] bg-surface" data-testid="basket-entries">
        <div className="flex items-baseline justify-between border-b border-[#1F1F1F] px-6 py-4">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{t("basket.entries")}</h3>
          <span className="font-mono text-[11px] text-zinc-600">{t("basket.entriesHint")}</span>
        </div>
        <div className="divide-y divide-[#141414]">
          {data.entries.length === 0 && (
            <p className="px-6 py-8 text-center text-sm text-zinc-600">{t("basket.noEntries")}</p>
          )}
          {data.entries.map((e) => (
            <div key={e.id} data-testid={`basket-entry-${e.id}`} className="flex items-center gap-4 px-6 py-3">
              <span className="w-28 shrink-0 font-mono text-xs text-zinc-500">{fmtDate(e.date)}</span>
              <span className="min-w-0 flex-1 font-mono text-xs text-zinc-200">
                {fmtUsd(e.usd)}
                {e.eth ? <span className="ml-2 text-zinc-500">{e.eth} ETH</span> : null}
                <span className="ml-2 text-zinc-600">
                  {e.lp_units} {data.lp_symbol || "LP"} · {fixed(e.share_pct, 2)}%
                </span>
              </span>
              <Button
                data-testid={`basket-entry-delete-${e.id}`}
                onClick={() => removeEntry(e.id)}
                className="h-8 w-8 shrink-0 rounded-none border border-[#1F1F1F] bg-transparent p-0 text-zinc-500 hover:bg-[#EF4444]/10 hover:text-[#EF4444]"
              >
                <Trash size={13} />
              </Button>
            </div>
          ))}
        </div>
        <div className="grid gap-4 border-t border-[#1F1F1F] px-6 py-5 sm:grid-cols-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t("basket.date")}</Label>
            <Input
              data-testid="basket-entry-date"
              type="datetime-local"
              value={entry.date}
              onChange={(e) => setEntry((p) => ({ ...p, date: e.target.value }))}
              className="rounded-none border-[#1F1F1F] bg-[#050505] font-mono focus-visible:ring-[#C58361]"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t("basket.usd")}</Label>
            <Input
              data-testid="basket-entry-usd"
              value={entry.usd}
              onChange={(e) => setEntry((p) => ({ ...p, usd: e.target.value }))}
              placeholder="29.25"
              className="rounded-none border-[#1F1F1F] bg-[#050505] font-mono focus-visible:ring-[#C58361]"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t("basket.eth")}</Label>
            <Input
              data-testid="basket-entry-eth"
              value={entry.eth}
              onChange={(e) => setEntry((p) => ({ ...p, eth: e.target.value }))}
              placeholder="0.01274"
              className="rounded-none border-[#1F1F1F] bg-[#050505] font-mono focus-visible:ring-[#C58361]"
            />
          </div>
          <div className="flex items-end">
            <Button
              data-testid="basket-entry-add-btn"
              onClick={addEntry}
              disabled={busy}
              className="h-10 w-full rounded-none border border-[#1F1F1F] bg-transparent text-sm text-zinc-200 hover:bg-[#111111]"
            >
              <Plus size={14} className="mr-2" /> {t("basket.addEntry")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
